/**
 * Cross-surface URL opener.
 *
 * Routes citations / web-search links to the user's default system browser
 * (Chrome / Safari / Edge) instead of trying to render them inside the task
 * pane's WebView.
 */

/**
 * Open a URL in the user's default system browser.
 *
 * Plain `window.open(url, '_blank')` is unreliable inside Word's WKWebView
 * (macOS) / WebView2 (Windows) — the WebView often intercepts the call and
 * opens a sandboxed mini-window that renders our add-in origin instead of the
 * target URL. `Office.context.ui.openBrowserWindow` is the documented escape
 * hatch and routes to the OS browser. On Word for Web it degrades gracefully
 * to `window.open`, which works there because the host is a real browser.
 */
export function openInSystemBrowser(url: string): void {
  if (!url) return;

  const api = (Office as unknown as {
    context?: { ui?: { openBrowserWindow?: (url: string) => void } };
  })?.context?.ui?.openBrowserWindow;

  if (typeof api === 'function') {
    try {
      api.call((Office as unknown as { context: { ui: unknown } }).context.ui, url);
      return;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[openInSystemBrowser] openBrowserWindow failed, falling back', err);
    }
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

// ---------------------------------------------------------------------------
// Draft-switcher: open a remote DOCX in a new Word window
// ---------------------------------------------------------------------------

/**
 * LocalStorage key that brokers the `chat_id` handoff from the current
 * plugin window to the *next* plugin window (the one Word is about to
 * launch via the `ms-word:ofe|u|` URI). The new window's TaskPane reads
 * this on mount, auto-links the fresh document's settings to the handed-off
 * chat, then clears the entry.
 */
const PENDING_DRAFT_LINK_KEY = 'nyayassist:pendingDraftLink';
const PENDING_DRAFT_TTL_MS = 60_000;

export interface PendingDraftLink {
  chatId: string;
  draftId?: string;
  name?: string;
  stampedAt: number;
}

export function writePendingDraftLink(payload: Omit<PendingDraftLink, 'stampedAt'>): void {
  try {
    window.localStorage.setItem(
      PENDING_DRAFT_LINK_KEY,
      JSON.stringify({ ...payload, stampedAt: Date.now() } satisfies PendingDraftLink),
    );
  } catch {
    /* storage disabled — caller logs its own fallback */
  }
}

/** Read + pop the pending link if still fresh. Returns null on miss. */
export function takePendingDraftLink(): PendingDraftLink | null {
  try {
    const raw = window.localStorage.getItem(PENDING_DRAFT_LINK_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingDraftLink;
    if (!parsed?.chatId || typeof parsed.stampedAt !== 'number') {
      window.localStorage.removeItem(PENDING_DRAFT_LINK_KEY);
      return null;
    }
    if (Date.now() - parsed.stampedAt > PENDING_DRAFT_TTL_MS) {
      window.localStorage.removeItem(PENDING_DRAFT_LINK_KEY);
      return null;
    }
    window.localStorage.removeItem(PENDING_DRAFT_LINK_KEY);
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Launch Word in a new window loaded with a remote DOCX.
 *
 * Mechanism:
 *   - On desktop (Windows / Mac) → `ms-word:ofe|u|<https-url>` URI scheme.
 *     Word registers this handler on install; navigating to it tells the OS
 *     to open the file in a fresh Word window. Requires HTTPS URL.
 *   - On Word for Web → no protocol handler exists. We fall back to opening
 *     the URL in the system browser, which triggers a normal download.
 *
 * The presigned URL should already have `response-content-disposition`
 * appended so Word uses a human-readable filename for the new window's title.
 */
/**
 * Open a DOCX in a new Word window using `Word.Application.createDocument(base64).open()`.
 *
 * Why this path, not `openDocument(url)`:
 *   `openDocument(url)` makes Word fetch the file at the given URL itself.
 *   On S3 presigned URLs that contain bare `|` characters in the path
 *   (common in our `google-oauth2|<sub>` key convention), Word's strict
 *   URL parser rejects the URL with a "Sorry, we couldn't open …" dialog.
 *   Word also shows a security "open from internet?" trust prompt first.
 *   Double-dialog UX is broken, and the signature can't be re-encoded
 *   client-side without breaking S3's HMAC check.
 *
 *   `createDocument(base64).open()` takes the bytes in memory we already
 *   have and opens them locally — no URL parsing, no trust prompt, no
 *   Content-Disposition confusion. Works on every documented Word build
 *   (WordApi 1.3+, Mac 15.32+ / Windows 2017+).
 */
export class NewWindowOpenError extends Error {
  constructor(message: string, public readonly cause?: unknown) {
    super(message);
    this.name = 'NewWindowOpenError';
  }
}

export async function openDocxInNewWordWindow(args: {
  base64Bytes: string;
}): Promise<void> {
  const { base64Bytes } = args;
  if (!base64Bytes) {
    throw new NewWindowOpenError('openDocxInNewWordWindow: base64Bytes required');
  }
  // eslint-disable-next-line no-console
  console.info('[openDocxInNewWordWindow] createDocument(base64).open()');
  try {
    await Word.run(async (context) => {
      const newDoc = context.application.createDocument(base64Bytes);
      await context.sync();
      newDoc.open();
      await context.sync();
    });
    // eslint-disable-next-line no-console
    console.info('[openDocxInNewWordWindow] succeeded');
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[openDocxInNewWordWindow] failed', err);
    throw new NewWindowOpenError(
      'Could not open a new Word window with this document. Your Word build may be older than WordApi 1.3 (Mac 15.32 / Windows 2017), or the document is malformed.',
      err,
    );
  }
}
